import {
  ContextType,
  ConversationCategory,
  ConversationMessageSource,
  ConversationParticipantRole,
  ConversationStatus,
  ConversationSummaryDTO,
  ConversationSupportStatus,
  ConversationType,
  LastMessageSenderKind
} from 'models/conversations'

import { isLastMessageAfterSupportStatusChange } from './conversationListStatus'

export const CONVERSATION_ASSISTANT_ID = '1172a9e7-0000-4000-8000-000000000002'

export enum OverviewGroup {
  REPLY_NEEDED = 'reply_needed',
  IN_PROGRESS = 'in_progress',
  OTHER = 'other',
  RESOLVED = 'resolved',
}

export enum OverviewMode {
  STATUS = 'status',
  ORDER = 'order',
}

export interface OrderReference {
  id: string
  number: string
  address?: string
}

export interface StatusSection {
  group: OverviewGroup
  items: ConversationSummaryDTO[]
}

export interface OrderSection {
  order?: OrderReference
  items: ConversationSummaryDTO[]
  replyNeededCount: number
}

const GROUP_RANK: Record<OverviewGroup, number> = {
  [OverviewGroup.REPLY_NEEDED]: 0,
  [OverviewGroup.IN_PROGRESS]: 1,
  [OverviewGroup.OTHER]: 2,
  [OverviewGroup.RESOLVED]: 3,
}

const STATUS_SECTION_ORDER: OverviewGroup[] = [
  OverviewGroup.REPLY_NEEDED,
  OverviewGroup.IN_PROGRESS,
  OverviewGroup.OTHER,
  OverviewGroup.RESOLVED,
]

const CATEGORY_LABEL_KEY: Record<ConversationCategory, string> = {
  [ConversationCategory.ACCOUNT]: 'chat:overview.category.account',
  [ConversationCategory.BILLING]: 'chat:overview.category.billing',
  [ConversationCategory.DELIVERY]: 'chat:overview.category.delivery',
  [ConversationCategory.DOWNLOADS]: 'chat:overview.category.downloads',
  [ConversationCategory.FEEDBACK]: 'chat:overview.category.feedback',
  [ConversationCategory.GENERAL]: 'chat:overview.category.general',
  [ConversationCategory.ORDERING]: 'chat:overview.category.ordering',
  [ConversationCategory.SCHEDULING]: 'chat:overview.category.scheduling',
}

export const getCategoryLabelKey = (conversation: ConversationSummaryDTO): string | undefined =>
  conversation.category ? CATEGORY_LABEL_KEY[conversation.category] : undefined

const ROLE_SENDER_KIND: Record<ConversationParticipantRole, LastMessageSenderKind> = {
  [ConversationParticipantRole.CLIENT]: LastMessageSenderKind.COLLEAGUE,
  [ConversationParticipantRole.CREATIVE]: LastMessageSenderKind.CREATIVE,
  [ConversationParticipantRole.AGENT]: LastMessageSenderKind.SUPPORT,
}

const isHumanTeamSource = (source: ConversationMessageSource | undefined) =>
  source === ConversationMessageSource.SLACK || source === ConversationMessageSource.SALESFORCE

export const resolveLastSenderKind = (
  conversation: ConversationSummaryDTO,
  currentUserId: string | undefined
): LastMessageSenderKind | undefined => {
  if (conversation.lastMessageSenderKind) return conversation.lastMessageSenderKind

  const senderId = conversation.lastMessageSenderId
  if (!senderId) return undefined
  if (currentUserId && senderId === currentUserId) return LastMessageSenderKind.YOU
  if (senderId === CONVERSATION_ASSISTANT_ID) return LastMessageSenderKind.ASSISTANT
  if (isHumanTeamSource(conversation.lastMessageSource)) return LastMessageSenderKind.SUPPORT

  const sender = conversation.participants.find((participant) => participant.userId === senderId)
  if (!sender) return undefined
  if (sender.role === ConversationParticipantRole.AGENT && conversation.type === ConversationType.AI_SUPPORT) {
    return LastMessageSenderKind.ASSISTANT
  }
  return ROLE_SENDER_KIND[sender.role]
}

export const getLastSenderName = (conversation: ConversationSummaryDTO): string | undefined =>
  conversation.participants.find((participant) => participant.userId === conversation.lastMessageSenderId)?.name

const SUPPORT_STATUS_GROUP: Record<ConversationSupportStatus, OverviewGroup> = {
  [ConversationSupportStatus.IN_PROGRESS]: OverviewGroup.IN_PROGRESS,
  [ConversationSupportStatus.WAITING_FOR_YOU]: OverviewGroup.REPLY_NEEDED,
  [ConversationSupportStatus.RESOLVED]: OverviewGroup.RESOLVED,
}

const isHandledBySupport = (conversation: ConversationSummaryDTO) =>
  conversation.supportStatus === ConversationSupportStatus.IN_PROGRESS ||
  conversation.supportStatus === ConversationSupportStatus.WAITING_FOR_YOU

export const isCase = (conversation: ConversationSummaryDTO): boolean =>
  conversation.type === ConversationType.AGENT_CLIENT ||
  conversation.type === ConversationType.CLIENT_CREATIVE ||
  conversation.status === ConversationStatus.ESCALATED ||
  conversation.workedByAgent === true ||
  isHandledBySupport(conversation)

const isLastFromSomeoneElse = (conversation: ConversationSummaryDTO, currentUserId: string | undefined) => {
  const kind = resolveLastSenderKind(conversation, currentUserId)
  if (kind) return kind !== LastMessageSenderKind.YOU
  return !!currentUserId && !!conversation.lastMessageSenderId && conversation.lastMessageSenderId !== currentUserId
}

const isLastFromCaller = (conversation: ConversationSummaryDTO, currentUserId: string | undefined) => {
  const kind = resolveLastSenderKind(conversation, currentUserId)
  if (kind) return kind === LastMessageSenderKind.YOU
  return !!currentUserId && conversation.lastMessageSenderId === currentUserId
}

const isWaitingForHumanTeam = (conversation: ConversationSummaryDTO, currentUserId: string | undefined) =>
  conversation.status === ConversationStatus.ESCALATED &&
  resolveLastSenderKind(conversation, currentUserId) !== LastMessageSenderKind.SUPPORT

export const getOverviewGroup = (
  conversation: ConversationSummaryDTO,
  currentUserId: string | undefined
): OverviewGroup => {
  if (!isCase(conversation)) return OverviewGroup.OTHER
  if (conversation.status === ConversationStatus.CLOSED) return OverviewGroup.RESOLVED
  const fromSupportStatus = conversation.supportStatus ? SUPPORT_STATUS_GROUP[conversation.supportStatus] : undefined
  if (
    fromSupportStatus === OverviewGroup.REPLY_NEEDED &&
    isLastFromCaller(conversation, currentUserId) &&
    isLastMessageAfterSupportStatusChange(conversation)
  ) {
    return OverviewGroup.IN_PROGRESS
  }
  if (fromSupportStatus) return fromSupportStatus
  if (isLastFromSomeoneElse(conversation, currentUserId) && !isWaitingForHumanTeam(conversation, currentUserId)) {
    return OverviewGroup.REPLY_NEEDED
  }
  return OverviewGroup.IN_PROGRESS
}

export const countReplyNeeded = (
  conversations: ConversationSummaryDTO[],
  currentUserId: string | undefined
): number => conversations.filter((conversation) => getOverviewGroup(conversation, currentUserId) === OverviewGroup.REPLY_NEEDED).length

const ORDER_NUMBER_IN_LABEL = /#\s*([\w-]+)/

export const getPropertyAddress = (conversation: ConversationSummaryDTO): string | undefined =>
  (conversation.contexts ?? []).find((context) => context.type === ContextType.PROPERTY && !!context.label)?.label

export const getOrderReference = (conversation: ConversationSummaryDTO): OrderReference | undefined => {
  const order = (conversation.contexts ?? []).find((context) => context.type === ContextType.ORDER)
  if (!order) return undefined
  const number = order.label?.match(ORDER_NUMBER_IN_LABEL)?.[1] ?? order.id
  return { id: order.id, number, address: getPropertyAddress(conversation) }
}

export const buildCardMeta = (
  conversation: ConversationSummaryDTO,
  mode: OverviewMode,
  categoryLabel?: string
): string => {
  if (mode === OverviewMode.ORDER) return categoryLabel ?? ''
  const order = getOrderReference(conversation)
  return [categoryLabel, order ? `#${order.number}` : undefined, order?.address ?? getPropertyAddress(conversation)]
    .filter(Boolean)
    .join(' · ')
}

export const matchesOverviewSearch = (
  conversation: ConversationSummaryDTO,
  displayTitle: string,
  query: string,
  categoryLabel?: string
): boolean => {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  const order = getOrderReference(conversation)
  const haystack = [
    displayTitle,
    conversation.lastMessagePreview,
    conversation.lastMessageAttachmentFilename,
    order?.number,
    order ? `#${order.number}` : undefined,
    getPropertyAddress(conversation),
    categoryLabel,
    ...(conversation.contexts ?? []).map((context) => context.label),
  ]
  return haystack.some((value) => !!value && value.toLowerCase().includes(needle))
}

export const mergeSearchResults = (
  localMatches: ConversationSummaryDTO[],
  serverMatches: ConversationSummaryDTO[],
  loaded: ConversationSummaryDTO[]
): ConversationSummaryDTO[] => {
  const listed = new Set(localMatches.map((conversation) => conversation.id))
  const loadedById = new Map(loaded.map((conversation) => [conversation.id, conversation]))
  const merged = [...localMatches]
  serverMatches.forEach((conversation) => {
    if (listed.has(conversation.id)) return
    listed.add(conversation.id)
    merged.push(loadedById.get(conversation.id) ?? conversation)
  })
  return merged
}

export const groupByStatus = (
  conversations: ConversationSummaryDTO[],
  currentUserId: string | undefined
): StatusSection[] =>
  STATUS_SECTION_ORDER
    .map((group) => ({
      group,
      items: conversations.filter((conversation) => getOverviewGroup(conversation, currentUserId) === group),
    }))
    .filter((section) => section.items.length > 0)

export const groupByOrder = (
  conversations: ConversationSummaryDTO[],
  currentUserId: string | undefined
): OrderSection[] => {
  const rankOf = (conversation: ConversationSummaryDTO) => GROUP_RANK[getOverviewGroup(conversation, currentUserId)]
  const positionOf = new Map(conversations.map((conversation, index) => [conversation.id, index]))
  const byOrderThenPosition = (a: ConversationSummaryDTO, b: ConversationSummaryDTO) =>
    rankOf(a) - rankOf(b) || (positionOf.get(a.id) ?? 0) - (positionOf.get(b.id) ?? 0)
  const byKey = new Map<string, { order?: OrderReference, items: ConversationSummaryDTO[] }>()

  conversations.forEach((conversation) => {
    const order = getOrderReference(conversation)
    const key = order ? `order:${order.id}` : 'none'
    const section = byKey.get(key) ?? { order, items: [] }
    if (order && !section.order?.address && order.address) section.order = order
    section.items.push(conversation)
    byKey.set(key, section)
  })

  const sections = Array.from(byKey.values()).map((section) => {
    const items = [...section.items].sort(byOrderThenPosition)
    return {
      order: section.order,
      items,
      replyNeededCount: items.filter((conversation) => getOverviewGroup(conversation, currentUserId) === OverviewGroup.REPLY_NEEDED).length,
    }
  })

  return sections.sort((a, b) => {
    if (!a.order) return 1
    if (!b.order) return -1
    return byOrderThenPosition(a.items[0], b.items[0])
  })
}
